import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase-client';
import { toggleBoardContentReaction } from '../../utils/boardApi';

export const BOARD_REACTION_EMOJIS = ['👍', '❤️', '😂', '🎉', '👀'];

function normalizeReactionCounts(value = {}) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return BOARD_REACTION_EMOJIS.reduce((counts, emoji) => {
    const count = Number(value[emoji] || 0);
    if (Number.isFinite(count) && count > 0) counts[emoji] = Math.floor(count);
    return counts;
  }, {});
}

function normalizeReactionSelection(value = []) {
  const source = Array.isArray(value) ? value : [];
  return BOARD_REACTION_EMOJIS.filter((emoji) => source.includes(emoji));
}

function getReactionDocRef({ teamId, boardId, topicId, contentType, contentId, currentUserId }) {
  if (contentType === 'reply') {
    return doc(db, 'teams', teamId, 'boards', boardId, 'topics', topicId, 'replies', contentId, 'reactions', currentUserId);
  }
  return doc(db, 'teams', teamId, 'boards', boardId, 'topics', contentId || topicId, 'reactions', currentUserId);
}

function buildOptimisticCounts(counts, previousEmojis, nextEmojis) {
  const nextCounts = { ...normalizeReactionCounts(counts) };
  const previous = new Set(normalizeReactionSelection(previousEmojis));
  const next = new Set(normalizeReactionSelection(nextEmojis));

  BOARD_REACTION_EMOJIS.forEach((emoji) => {
    let count = Number(nextCounts[emoji] || 0);
    if (previous.has(emoji) && !next.has(emoji)) count -= 1;
    if (!previous.has(emoji) && next.has(emoji)) count += 1;
    if (count > 0) nextCounts[emoji] = count;
    else delete nextCounts[emoji];
  });

  return nextCounts;
}

function reactionCountsEqual(left = {}, right = {}) {
  const normalizedLeft = normalizeReactionCounts(left);
  const normalizedRight = normalizeReactionCounts(right);
  return BOARD_REACTION_EMOJIS.every((emoji) => (
    Number(normalizedLeft[emoji] || 0) === Number(normalizedRight[emoji] || 0)
  ));
}

export default function BoardReactionBar({
  teamId,
  boardId,
  topicId,
  content,
  contentType,
  contentId,
  currentUserId,
  canReact = true,
}) {
  const [myEmojis, setMyEmojis] = useState([]);
  const [localCounts, setLocalCounts] = useState(null);
  const [busyEmoji, setBusyEmoji] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAlignEnd, setMenuAlignEnd] = useState(false);
  const [error, setError] = useState('');
  const menuRef = useRef(null);

  const contentDocId = contentId || content?.id || '';
  const contentAuthorId = content?.authorId || '';
  const contentModerationStatus = String(content?.moderationStatus || 'visible');
  const contentPending = Boolean(content?.pending);
  const canReactToContent = Boolean(
    canReact
    && currentUserId
    && teamId
    && boardId
    && topicId
    && contentDocId
    && !contentPending
    && contentModerationStatus === 'visible'
    && contentAuthorId !== currentUserId
  );
  const serverCounts = useMemo(() => normalizeReactionCounts(content?.reactionCounts), [content?.reactionCounts]);
  const reactionCounts = localCounts || serverCounts;
  const visibleEmojis = BOARD_REACTION_EMOJIS.filter((emoji) => Number(reactionCounts[emoji] || 0) > 0);

  useEffect(() => {
    if (localCounts && reactionCountsEqual(serverCounts, localCounts)) {
      setLocalCounts(null);
    }
  }, [localCounts, serverCounts]);

  useEffect(() => {
    setLocalCounts(null);
  }, [boardId, contentDocId, contentType, currentUserId, teamId, topicId]);

  useEffect(() => {
    if (!canReactToContent) {
      setMyEmojis([]);
      return undefined;
    }

    let cancelled = false;
    getDoc(getReactionDocRef({
      teamId,
      boardId,
      topicId,
      contentType,
      contentId: contentDocId,
      currentUserId,
    })).then((snap) => {
      if (cancelled) return;
      setMyEmojis(snap.exists() ? normalizeReactionSelection(snap.data()?.emojis) : []);
    }).catch(() => {
      if (!cancelled) setMyEmojis([]);
    });

    return () => {
      cancelled = true;
    };
  }, [boardId, canReactToContent, contentDocId, contentType, currentUserId, teamId, topicId]);

  const updateMenuAlignment = useCallback(() => {
    const bounds = menuRef.current?.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    if (!bounds || !viewportWidth) return;
    setMenuAlignEnd((bounds.left + bounds.width / 2) > (viewportWidth / 2));
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;
    updateMenuAlignment();
    const handlePointerDown = (event) => {
      if (!menuRef.current?.contains(event.target)) setMenuOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };

    window.addEventListener('resize', updateMenuAlignment);
    window.addEventListener('orientationchange', updateMenuAlignment);
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('resize', updateMenuAlignment);
      window.removeEventListener('orientationchange', updateMenuAlignment);
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen, updateMenuAlignment]);

  const handleToggle = async (emoji) => {
    if (!canReactToContent || busyEmoji) return;
    const previousEmojis = myEmojis;
    const nextEmojis = previousEmojis.includes(emoji)
      ? previousEmojis.filter((entry) => entry !== emoji)
      : [...previousEmojis, emoji];
    const previousCounts = reactionCounts;

    setBusyEmoji(emoji);
    setError('');
    setMyEmojis(nextEmojis);
    setLocalCounts(buildOptimisticCounts(previousCounts, previousEmojis, nextEmojis));
    try {
      const result = await toggleBoardContentReaction({
        teamId,
        boardId,
        topicId,
        contentType,
        contentId: contentDocId,
        emoji,
      });
      setMyEmojis(normalizeReactionSelection(result?.emojis));
      setLocalCounts(normalizeReactionCounts(result?.reactionCounts));
      setMenuOpen(false);
    } catch (err) {
      setMyEmojis(previousEmojis);
      setLocalCounts(previousCounts);
      setError(err?.message || 'Could not update reaction.');
    } finally {
      setBusyEmoji('');
    }
  };

  if (visibleEmojis.length === 0 && !canReactToContent) return null;

  return (
    <div className="board-reaction-bar">
      {visibleEmojis.map((emoji) => {
        const active = myEmojis.includes(emoji);
        const count = Number(reactionCounts[emoji] || 0);
        return (
          <button
            key={emoji}
            type="button"
            className={`board-reaction-pill ${active ? 'is-active' : ''}`}
            aria-pressed={active}
            aria-label={`${active ? 'Remove' : 'Add'} ${emoji} reaction`}
            title={`${emoji} ${count}`}
            disabled={!canReactToContent || Boolean(busyEmoji)}
            onClick={() => handleToggle(emoji)}
          >
            <span aria-hidden="true">{emoji}</span>
            <span>{count}</span>
          </button>
        );
      })}
      {canReactToContent && (
        <div className={`board-reaction-picker ${menuAlignEnd ? 'is-aligned-end' : 'is-aligned-start'}`} ref={menuRef}>
          <button
            type="button"
            className="board-reaction-add"
            aria-label="Add reaction"
            title="Add reaction"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            disabled={Boolean(busyEmoji)}
            onClick={() => {
              if (!menuOpen) updateMenuAlignment();
              setMenuOpen((open) => !open);
            }}
          >
            +
          </button>
          {menuOpen && (
            <div className="board-reaction-picker__menu" role="menu" aria-label="Choose reaction">
              {BOARD_REACTION_EMOJIS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  className={`board-reaction-picker__item ${myEmojis.includes(emoji) ? 'is-active' : ''}`}
                  role="menuitem"
                  disabled={Boolean(busyEmoji)}
                  onClick={() => handleToggle(emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {error && <span className="board-reaction-bar__error">{error}</span>}
    </div>
  );
}
